const colorToRgb = (color: string): { r: number; g: number; b: number } | null => {
  // Handle hex colors
  const hexMatch = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
  if (hexMatch) {
    return {
      r: Number.parseInt(hexMatch[1], 16),
      g: Number.parseInt(hexMatch[2], 16),
      b: Number.parseInt(hexMatch[3], 16),
    };
  }

  // Handle rgb/rgba colors
  const rgbMatch = color.match(/\d+/g);
  if (rgbMatch && rgbMatch.length >= 3) {
    return {
      r: Number.parseInt(rgbMatch[0]),
      g: Number.parseInt(rgbMatch[1]),
      b: Number.parseInt(rgbMatch[2]),
    };
  }

  return null;
};

// Calculate relative luminance (WCAG formula)
const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((val) => {
    const s = val / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Calculate contrast ratio between two colors
const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = colorToRgb(color1);
  const rgb2 = colorToRgb(color2);

  if (!rgb1 || !rgb2) return 1;

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
};

export const hasGoodContrast = (
  color: string,
  backgroundColor: string,
  threshold: number = 1.5,
): boolean => {
  return getContrastRatio(color, backgroundColor) > threshold;
};
